import { createContext, ReactNode, useEffect, useMemo } from 'react';
import { useConnection } from 'wagmi';
import {
  useUserDetails,
  useRefreshUser,
  useVerifyUser,
  OwnerInfo,
  VerifyUserErrorType,
} from 'structure/launchpad/hooks/usePortfolio';
import { UserTier } from 'structure/portfolio/constants/tiers';
import { useDashboardInfo } from 'structure/dashboard/hooks/useDashboardAPI';
import { DashboardInfo } from 'api/DashboardAPI';
export interface ChainDetail {
  chainId: number;
  explorerUrl: string;
  iconUrl: string;
  name: string;
}
export interface TokenDetail {
  address: string;
  decimals: number;
  icon_url: string;
  name: string;
  symbol: string;
  chainId: number;
}

interface AuthContextType {
  verified: boolean;
  address: string | undefined;
  verifyUser: () => Promise<OwnerInfo | null>;
  verifiedData: OwnerInfo | null;
  verifyLoading: boolean;
  verifyError: VerifyUserErrorType | null;
  userDetails: UserDetail | undefined;
  refreshUser: () => void;
  isUpgraded: boolean;
  dashboardInfo: DashboardInfo | undefined;
  isUserDetailsLoading: boolean;
}

interface TokenBalance {
  balance?: string;
  chainId?: number;
  usdValue?: string;
  tokenAddress?: string;
  formattedBalance?: string;
  symbol?: string;
}

interface StakingToken {
  symbol: string;
}

interface Staking {
  tvl?: number;
  tokens?: StakingToken[];
  chainId?: number;
  staking_type?: string;
  staking_contract?: string;
  name?: string;
  icon?: string;
  price?: number;
  unlockDate?: string;
  apr?: string;
  usdValue?: number;
  contractAddress?: string;
  token?: string;
  tokeName?: string;
  claimable?: string;
  totalStaked?: string;
  tokenDecimals?: number;
  reputationScore?: string;
  priceInUSD?: string;
}

export interface UserDetail {
  id?: string;
  attributes?: Record<string, unknown>;
  primaryAddress?: string;
  dailytvlChange?: string;
  createdAt?: string;
  updatedAt?: string;
  totalValue?: string;
  portfolioValue?: string;
  claimingBalance?: string;
  stakingBalance?: string;
  vaultBalance?: string;
  vaultShares?: Record<string, unknown> | unknown[];
  tokenBalances?: TokenBalance[];
  launchpadBalances?: Record<string, unknown> | unknown[];
  lastSyncedAt?: string | null;
  tier?: UserTier;
  SREP?: string;
  xp?: string;
  portfolioChange?: string;
  staking?: Staking[];
}

export const AuthContext = createContext<AuthContextType>({
  verified: false,
  address: '',
  verifyUser: async () => null,
  verifiedData: null,
  verifyLoading: false,
  verifyError: null,
  userDetails: undefined,
  refreshUser: () => {},
  isUpgraded: false,
  dashboardInfo: undefined,
  isUserDetailsLoading: false,
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { address, chain } = useConnection();

  const {
    verifyUser,
    verifiedData,
    verified,
    verifyLoading,
    setVerified,
    verifyError,
  } = useVerifyUser(address ?? '', chain?.id ?? 0);

  const { data: dashboardInfo } = useDashboardInfo({
    walletAddress: address ?? '',
  });

  const {
    data: userDetails,
    refetch: fetchUserDetails,
    isFetching: isUserDetailsLoading,
    isError: isUserDetailsError,
  } = useUserDetails(verified, address as string | undefined);

  useEffect(() => {
    if (isUserDetailsError) {
      setVerified(false);
    }
  }, [isUserDetailsError, setVerified]);

  const isUpgraded = useMemo(() => {
    return (
      userDetails?.tier !== undefined &&
      [UserTier.GOLD, UserTier.DIAMOND].includes(userDetails.tier)
    );
  }, [userDetails]);

  const { data: refreshData, refetch: refreshUser } = useRefreshUser(
    address as string | undefined,
    isUpgraded
  );

  useEffect(() => {
    if (refreshData?.refreshData) {
      fetchUserDetails();
    }
  }, [refreshData, fetchUserDetails]);

  return (
    <AuthContext.Provider
      value={{
        verified,
        address,
        verifyUser,
        verifiedData,
        verifyLoading,
        verifyError,
        userDetails: userDetails ?? undefined,
        refreshUser,
        isUpgraded,
        dashboardInfo,
        isUserDetailsLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
